export type KaijuId = 'ghidorah' | 'mothra' | 'mechagodzilla' | 'rodan';
export type TriageTag = 'red' | 'yellow' | 'green' | 'black';

const TAG_TO_CATEGORY: Record<TriageTag, string> = {
  red: 'immediate', yellow: 'delayed', green: 'minor', black: 'expectant'
};

const waveTimerReduction: Record<number, Partial<Record<string, number>>> = {
  1: { immediate: 0,  delayed: 0,  minor: 0 },
  2: { immediate: 4,  delayed: 5,  minor: 0 },
  3: { immediate: 8,  delayed: 10, minor: 2 },
  4: { immediate: 12, delayed: 15, minor: 4 },
  5: { immediate: 16, delayed: 20, minor: 6 },
  6: { immediate: 20, delayed: 24, minor: 8 },
};

export function getTimer(tag: TriageTag, wave: number, baseTimers: Record<TriageTag, number>): number {
  const category = TAG_TO_CATEGORY[tag];
  const base = baseTimers[tag];
  const reduction = waveTimerReduction[Math.min(wave, 6)];
  if (!reduction || reduction[category] === undefined) return base;
  return Math.max(base - reduction[category]!, 14);
}
export type PatientStatus = 'stable' | 'deteriorating' | 'critical';
export type Algorithm = 'START' | 'SALT';

export interface Kaiju {
  id: KaijuId;
  name: string;
  emoji: string;
  description: string;
  injuryTypes: string[];
  waveMessages: string[];
}

export type AvpuScore = 'A' | 'V' | 'P' | 'Pi' | 'U';

export interface Patient {
  id: string;
  name: string;
  age: number;
  mechanism: string;
  vitals: {
    hr: number;
    bp: string;
    rr: number;
    spo2: number;
    gcs: number;
  };
  symptoms: string[];
  correctTag: TriageTag;
  timerDuration: number; // seconds
  timeRemaining: number;
  status: PatientStatus;
  deteriorated: boolean;
  triaged: boolean;
  assignedTag?: TriageTag;
  deterioratedTag?: TriageTag; // what the patient deteriorated to
  outcomeMessage?: string; // flash message for wrong/expired outcomes
  pointDelta?: number; // points earned/lost for this triage
  penaltyMessages?: string[]; // penalty reasons from triage flow
  // START algorithm decision tree fields
  canWalk: boolean;
  isBreathing: boolean;
  breathesAfterReposition: boolean;
  hasRadialPulse: boolean;
  capRefill: number;
  followsCommands: boolean;
  // JumpSTART (pediatric) fields
  isPediatric: boolean;
  avpuScore: AvpuScore;
  respondsToRescueBreaths: boolean;
  rrAfterRescueBreaths?: number;
}

export const KAIJU: Record<KaijuId, Kaiju> = {
  ghidorah: {
    id: 'ghidorah',
    name: 'King Ghidorah',
    emoji: '⚡',
    description: 'Three-headed lightning dragon — expect electrical burns, blast injuries, and cardiac arrhythmias',
    injuryTypes: [
      'Electrical burn from Ghidorah lightning strike',
      'Blast injury from gravity beam explosion',
      'Cardiac arrest after lightning strike exposure',
      'Flash blindness from energy beam',
      'Crush injury — building collapsed from gravity beam',
      'Shrapnel wounds from electrical infrastructure explosion',
      'Severe burns — caught in lightning storm radius',
      'Traumatic amputation from structural collapse',
    ],
    waveMessages: [
      'Ghidorah just unleashed a triple gravity beam across downtown — mass electrical casualties incoming!',
      'Lightning storm intensifying — power grid explosions across the east side!',
      'Godzilla knocked into the financial district by Ghidorah — mass crush injuries incoming!',
      'Ghidorah hovering over the hospital district — evacuations failing, more casualties!',
      'FINAL WAVE — Ghidorah going full power, city-wide devastation!',
    ],
  },
  mothra: {
    id: 'mothra',
    name: 'Mothra',
    emoji: '🦋',
    description: 'Giant divine moth — expect wing gust trauma, toxic scale powder exposure, and sonic injuries',
    injuryTypes: [
      'Toxic scale powder inhalation — respiratory distress',
      'Thrown by wing gust — blunt force trauma',
      'Sonic boom injury from Mothra screech',
      'Allergic anaphylaxis from bioluminescent dust',
      'Crush injury — vehicle launched by wing blast',
      'Chemical burns from moth silk secretion',
      'Penetrating wound from debris in wind vortex',
      'Hypothermia — trapped in cocoon web structure',
    ],
    waveMessages: [
      'Mothra released a toxic scale cloud over the south side — respiratory casualties flooding in!',
      'Wing gusts reaching hurricane force — mass blunt trauma incoming!',
      'Godzilla and Mothra clashing mid-air — debris raining on residential areas!',
      'Mothra cocoon webs trapping civilians — extraction teams overwhelmed!',
      'FINAL WAVE — Mothra divine judgment mode, city-wide scale storm!',
    ],
  },
  mechagodzilla: {
    id: 'mechagodzilla',
    name: 'Mechagodzilla',
    emoji: '🤖',
    description: 'Cybernetic war machine — expect missile blast injuries, radiation exposure, and heavy metal contamination',
    injuryTypes: [
      'Missile blast injury — multiple shrapnel wounds',
      'Radiation exposure from proton scream beam',
      'Heavy metal contamination from mecha debris',
      'Crush injury — buried under titanium wreckage',
      'Thermal burn from jet exhaust blast',
      'Concussion from sonic weaponry',
      'Penetrating chest wound from mecha fragment',
      'Chemical exposure — hydraulic fluid contamination',
    ],
    waveMessages: [
      'Mechagodzilla launched a missile barrage at the waterfront — mass blast casualties!',
      'Proton scream beam swept through midtown — radiation exposure zone expanding!',
      'Godzilla ripped off Mechagodzilla\'s arm — titanium debris everywhere!',
      'Mechagodzilla self-repair systems overloading — chemical leaks across the industrial zone!',
      'FINAL WAVE — Mechagodzilla going nuclear, city-wide threat level maximum!',
    ],
  },
  rodan: {
    id: 'rodan',
    name: 'Rodan',
    emoji: '🔥',
    description: 'Volcanic pteranodon — expect thermal burns, volcanic ash inhalation, and impact trauma from supersonic flyovers',
    injuryTypes: [
      'Severe thermal burn from volcanic heat wake',
      'Volcanic ash inhalation — acute respiratory failure',
      'Impact trauma from supersonic shockwave',
      'Crush injury — building toppled by flyover turbulence',
      'Lava splash burn from volcanic debris',
      'Tympanic membrane rupture from sonic boom',
      'Embedded volcanic rock fragment wounds',
      'Heat stroke from radiant thermal exposure',
    ],
    waveMessages: [
      'Rodan just did a supersonic flyover — shockwave flattened six blocks!',
      'Volcanic ash cloud descending on the city — respiratory emergencies surging!',
      'Godzilla and Rodan locked in combat over the river — thermal shockwaves everywhere!',
      'Rodan nesting on the radio tower — lava dripping on surrounding buildings!',
      'FINAL WAVE — Rodan volcanic eruption mode, the sky is on fire!',
    ],
  },
};

export const FIRST_NAMES = [
  'James', 'Maria', 'Chen', 'Aisha', 'Takeshi', 'Elena', 'Marcus', 'Yuki',
  'Sarah', 'Diego', 'Priya', 'Oluwaseun', 'Alex', 'Fatima', 'Kai', 'Lucia',
  'Ben', 'Mei', 'Omar', 'Ingrid', 'Raj', 'Amara', 'Tommy', 'Sophie',
  'Daisuke', 'Nadia', 'Patrick', 'Hana', 'Viktor', 'Rosa',
];

export const LAST_NAMES = [
  'Tanaka', 'Rodriguez', 'Chen', 'Williams', 'Patel', 'Nakamura', 'Jensen',
  'Kim', 'Okafor', 'Müller', 'Santos', 'Park', 'Hernandez', 'Watanabe',
  'Singh', 'Sato', 'Lee', 'Brown', 'Yamamoto', 'Petrov',
];

let patientCounter = 0;

export interface PatientTemplate {
  correctTag: TriageTag;
  vitals: Patient['vitals'];
  symptoms: string[];
  status: PatientStatus;
  // START algorithm fields
  canWalk: boolean;
  isBreathing: boolean;
  breathesAfterReposition: boolean;
  hasRadialPulse: boolean;
  capRefill: number;
  followsCommands: boolean;
  // JumpSTART (pediatric) fields
  isPediatric?: boolean;
  avpuScore?: AvpuScore;
  respondsToRescueBreaths?: boolean;
  rrAfterRescueBreaths?: number;
}

export const TAG_TEMPLATES: Record<TriageTag, PatientTemplate[]> = {
  red: [
    {
      correctTag: 'red',
      vitals: { hr: 130, bp: '80/50', rr: 32, spo2: 85, gcs: 10 },
      symptoms: ['Severe hemorrhage', 'Altered consciousness', 'Tachypnea'],
      status: 'critical',
      canWalk: false, isBreathing: true, breathesAfterReposition: true,
      hasRadialPulse: false, capRefill: 4, followsCommands: true,
    },
    {
      correctTag: 'red',
      vitals: { hr: 140, bp: '70/40', rr: 8, spo2: 78, gcs: 8 },
      symptoms: ['Tension pneumothorax', 'Cyanosis', 'Absent breath sounds left'],
      status: 'critical',
      canWalk: false, isBreathing: true, breathesAfterReposition: true,
      hasRadialPulse: true, capRefill: 4, followsCommands: false,
    },
    {
      correctTag: 'red',
      vitals: { hr: 150, bp: '60/30', rr: 35, spo2: 82, gcs: 9 },
      symptoms: ['Massive abdominal bleeding', 'Rigid abdomen', 'Shock'],
      status: 'critical',
      canWalk: false, isBreathing: true, breathesAfterReposition: true,
      hasRadialPulse: false, capRefill: 5, followsCommands: true,
    },
  ],
  yellow: [
    {
      correctTag: 'yellow',
      vitals: { hr: 100, bp: '110/70', rr: 22, spo2: 93, gcs: 14 },
      symptoms: ['Open femur fracture', 'Moderate pain', 'Alert and oriented'],
      status: 'stable',
      canWalk: false, isBreathing: true, breathesAfterReposition: true,
      hasRadialPulse: true, capRefill: 2, followsCommands: true,
    },
    {
      correctTag: 'yellow',
      vitals: { hr: 95, bp: '120/80', rr: 20, spo2: 94, gcs: 15 },
      symptoms: ['Deep laceration with controlled bleeding', 'Burns 15% TBSA', 'Pain 7/10'],
      status: 'stable',
      canWalk: false, isBreathing: true, breathesAfterReposition: true,
      hasRadialPulse: true, capRefill: 2, followsCommands: true,
    },
    {
      correctTag: 'yellow',
      vitals: { hr: 105, bp: '100/65', rr: 24, spo2: 92, gcs: 13 },
      symptoms: ['Suspected spinal injury', 'Loss of sensation in legs', 'Awake'],
      status: 'deteriorating',
      canWalk: false, isBreathing: true, breathesAfterReposition: true,
      hasRadialPulse: true, capRefill: 2, followsCommands: true,
    },
  ],
  green: [
    {
      correctTag: 'green',
      vitals: { hr: 85, bp: '130/85', rr: 16, spo2: 98, gcs: 15 },
      symptoms: ['Minor lacerations', 'Walking wounded', 'Anxious but stable'],
      status: 'stable',
      canWalk: true, isBreathing: true, breathesAfterReposition: true,
      hasRadialPulse: true, capRefill: 2, followsCommands: true,
    },
    {
      correctTag: 'green',
      vitals: { hr: 78, bp: '125/80', rr: 14, spo2: 99, gcs: 15 },
      symptoms: ['Wrist sprain', 'Superficial abrasions', 'Ambulatory'],
      status: 'stable',
      canWalk: true, isBreathing: true, breathesAfterReposition: true,
      hasRadialPulse: true, capRefill: 2, followsCommands: true,
    },
    {
      correctTag: 'green',
      vitals: { hr: 90, bp: '120/75', rr: 18, spo2: 97, gcs: 15 },
      symptoms: ['Smoke inhalation — mild', 'Coughing', 'Clear lungs on auscultation'],
      status: 'stable',
      canWalk: true, isBreathing: true, breathesAfterReposition: true,
      hasRadialPulse: true, capRefill: 2, followsCommands: true,
    },
  ],
  black: [
    {
      correctTag: 'black',
      vitals: { hr: 0, bp: '0/0', rr: 0, spo2: 0, gcs: 3 },
      symptoms: ['No pulse', 'No respirations', 'Incompatible injuries with life'],
      status: 'critical',
      canWalk: false, isBreathing: false, breathesAfterReposition: false,
      hasRadialPulse: false, capRefill: 0, followsCommands: false,
    },
    {
      correctTag: 'black',
      vitals: { hr: 0, bp: '0/0', rr: 0, spo2: 0, gcs: 3 },
      symptoms: ['Massive crush injury', 'No signs of life', 'Pupil fixed and dilated'],
      status: 'critical',
      canWalk: false, isBreathing: false, breathesAfterReposition: false,
      hasRadialPulse: false, capRefill: 0, followsCommands: false,
    },
  ],
};

function randomFrom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

// Shuffle-bag: deal from a shuffled deck to avoid repeats until pool is exhausted
const injuryBags = new Map<string, string[]>();

function shuffleArray<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function pickInjury(pool: { text: string; tags: TriageTag[] }[], bagKey: string): string {
  let bag = injuryBags.get(bagKey);
  if (!bag || bag.length === 0) {
    bag = shuffleArray(pool.map(i => i.text));
    injuryBags.set(bagKey, bag);
  }
  return bag.pop()!;
}

export function resetInjuryBags() {
  injuryBags.clear();
}

import { loadJSON as loadOverride } from './settingsCache';



export function generatePatient(kaijuId: KaijuId, wave: number, forceTag?: TriageTag): Patient {
  const templates: Record<TriageTag, PatientTemplate[]> = loadOverride('mci-admin-templates') ?? TAG_TEMPLATES;
  const firstNames: string[] = loadOverride('mci-admin-first-names') ?? FIRST_NAMES;
  const lastNames: string[] = loadOverride('mci-admin-last-names') ?? LAST_NAMES;
  const timers: Record<TriageTag, number> = loadOverride('mci-admin-timers') ?? { red: 22, yellow: 40, green: 55, black: 30 };

  // Use forceTag if provided, otherwise random from templates
  const tag: TriageTag = forceTag ?? (Object.keys(templates) as TriageTag[])[Math.floor(Math.random() * 4)];

  const tagTemplates = templates[tag];

  // Pediatric roll: split templates into adult/peds pools
  const allPedsOverrides = loadOverride<Record<string, Record<number, number>>>('mci-admin-peds-percent');
  const kaijuPeds = allPedsOverrides?.[kaijuId];
  const waveCfg = WAVE_CONFIG[Math.min(wave, 6)];
  const pedsChance = kaijuPeds?.[wave] ?? kaijuPeds?.[Math.min(wave, 6)] ?? waveCfg.pediatricPercent;
  const adultPool = tagTemplates.filter(t => !t.isPediatric);
  const pedsPool = tagTemplates.filter(t => t.isPediatric);
  const wantPeds = Math.random() < pedsChance;
  const chosenPool = wantPeds && pedsPool.length > 0 ? pedsPool : adultPool.length > 0 ? adultPool : tagTemplates;
  const template = randomFrom(chosenPool);

  // Load injuries with tag filtering (kaiju-specific + shared)
  const injuryRaw = loadOverride<Record<string, unknown>>('mci-admin-injuries');
  const sharedRaw = loadOverride<unknown[]>('mci-admin-shared-injuries');
  const rawEntries = injuryRaw?.[kaijuId];

  // Build combined enriched pool
  const toEnriched = (raw: unknown): { text: string; tags: TriageTag[] }[] => {
    if (!Array.isArray(raw) || raw.length === 0) return [];
    if (typeof raw[0] === 'object' && raw[0] !== null && 'text' in raw[0]) return raw as { text: string; tags: TriageTag[] }[];
    return (raw as string[]).map(t => ({ text: t, tags: [] as TriageTag[] }));
  };

  const kaijuInjuries = rawEntries ? toEnriched(rawEntries) : KAIJU[kaijuId].injuryTypes.map(t => ({ text: t, tags: [] as TriageTag[] }));
  const sharedInjuries = sharedRaw ? toEnriched(sharedRaw) : [];
  const allInjuries = [...kaijuInjuries, ...sharedInjuries];

  let mechanism: string;
  if (allInjuries.length > 0) {
    const tagged = allInjuries.filter(i => i.tags.length === 0 || i.tags.includes(tag));
    const pool = tagged.length > 0 ? tagged : allInjuries;
    const bagKey = `${kaijuId}-${tag}`;
    mechanism = pickInjury(pool, bagKey);
  } else {
    mechanism = randomFrom(KAIJU[kaijuId].injuryTypes);
  }

  // Timer rules by severity (wave-based reduction)
  const timerDuration = getTimer(tag, wave, timers);

  patientCounter++;

  return {
    id: `patient-${patientCounter}-${Date.now()}`,
    name: `${randomFrom(firstNames)} ${randomFrom(lastNames)}`,
    age: template.isPediatric
      ? Math.floor(Math.random() * 14) + 1
      : Math.floor(Math.random() * 60) + 18,
    mechanism,
    vitals: { ...template.vitals },
    symptoms: [...template.symptoms],
    correctTag: template.correctTag,
    timerDuration,
    timeRemaining: timerDuration,
    status: template.status,
    deteriorated: false,
    triaged: false,
    canWalk: template.canWalk,
    isBreathing: template.isBreathing,
    breathesAfterReposition: template.breathesAfterReposition,
    hasRadialPulse: template.hasRadialPulse,
    capRefill: template.capRefill ?? 2,
    followsCommands: template.followsCommands,
    isPediatric: template.isPediatric ?? false,
    avpuScore: template.avpuScore ?? 'A',
    respondsToRescueBreaths: template.respondsToRescueBreaths ?? false,
    rrAfterRescueBreaths: template.rrAfterRescueBreaths ?? template.vitals.rr,
  };
}

export interface WaveConfigEntry {
  immediatePercent: number;
  expectantPercent: number;
  spawnBurstSize: { min: number; max: number };
  staggerInterval: { base: number; jitter: number };
  cascadeChance: number;
  cascadePlusImmediateChance: number;
  pediatricPercent: number;
}

export const WAVE_CONFIG: Record<number, WaveConfigEntry> = {
  1: { immediatePercent: 0.10, expectantPercent: 0.00, spawnBurstSize: { min: 2, max: 3 }, staggerInterval: { base: 40000, jitter: 5000 }, cascadeChance: 0, cascadePlusImmediateChance: 0, pediatricPercent: 0.10 },
  2: { immediatePercent: 0.20, expectantPercent: 0.05, spawnBurstSize: { min: 4, max: 5 }, staggerInterval: { base: 30000, jitter: 5000 }, cascadeChance: 0.10, cascadePlusImmediateChance: 0, pediatricPercent: 0.15 },
  3: { immediatePercent: 0.30, expectantPercent: 0.10, spawnBurstSize: { min: 8, max: 10 }, staggerInterval: { base: 20000, jitter: 5000 }, cascadeChance: 0.20, cascadePlusImmediateChance: 0, pediatricPercent: 0.20 },
  4: { immediatePercent: 0.35, expectantPercent: 0.15, spawnBurstSize: { min: 10, max: 12 }, staggerInterval: { base: 15000, jitter: 3000 }, cascadeChance: 0.40, cascadePlusImmediateChance: 0.05, pediatricPercent: 0.25 },
  5: { immediatePercent: 0.40, expectantPercent: 0.20, spawnBurstSize: { min: 12, max: 15 }, staggerInterval: { base: 10000, jitter: 2000 }, cascadeChance: 0.60, cascadePlusImmediateChance: 0.10, pediatricPercent: 0.25 },
  6: { immediatePercent: 0.40, expectantPercent: 0.20, spawnBurstSize: { min: 15, max: 20 }, staggerInterval: { base: 8000, jitter: 2000 }, cascadeChance: 0.70, cascadePlusImmediateChance: 0.15, pediatricPercent: 0.30 },
};

// Ghidorah trickle weights: cumulative probability arrays for 1–N patients per trickle
const GHIDORAH_TRICKLE: Record<number, number[]> = {
  1: [0.55, 0.85, 0.95, 1.0],       // max 4, avg ~1.65
  2: [0.40, 0.70, 0.90, 1.0],       // max 4, avg ~2.0
  3: [0.30, 0.55, 0.80, 1.0],       // max 4, avg ~2.35
  4: [0.20, 0.45, 0.75, 1.0],       // max 4, avg ~2.6
  5: [0.10, 0.30, 0.65, 1.0],       // max 4, avg ~2.95
  6: [0.05, 0.20, 0.55, 1.0],       // max 4, avg ~3.2
};

// MechaGodzilla trickle weights: cumulative probability arrays for 1–3 patients per trickle
const MECHAGODZILLA_TRICKLE: Record<number, number[]> = {
  1: [0.65, 0.90, 1.0],   // max 3, avg ~1.45
  2: [0.50, 0.85, 1.0],   // max 3, avg ~1.65
  3: [0.40, 0.75, 1.0],   // max 3, avg ~1.85
  4: [0.30, 0.65, 1.0],   // max 3, avg ~2.05
  5: [0.20, 0.55, 1.0],   // max 3, avg ~2.25
  6: [0.10, 0.45, 1.0],   // max 3, avg ~2.45
};

export function getWaveConfig(wave: number, kaijuId?: KaijuId) {
  // Per-kaiju wave config takes priority, then global, then default
  const DEFAULT_PATIENTS_PER_WAVE = [10, 10, 10, 10, 10];
  let patientsPerWave: number[];
  if (kaijuId) {
    const kaijuWaves = loadOverride<Record<string, number[]>>('mci-admin-kaiju-waves');
    patientsPerWave = kaijuWaves?.[kaijuId] ?? DEFAULT_PATIENTS_PER_WAVE;
  } else {
    patientsPerWave = DEFAULT_PATIENTS_PER_WAVE;
  }
  const idx = Math.min(wave - 1, patientsPerWave.length - 1);
  const patientCount = patientsPerWave[idx] ?? 10;
  const config = WAVE_CONFIG[Math.min(wave, 6)];

  // Merge Ghidorah trickle weights if applicable
  const trickleWeights = kaijuId === 'ghidorah'
    ? GHIDORAH_TRICKLE[Math.min(wave, 6)]
    : kaijuId === 'mechagodzilla'
      ? MECHAGODZILLA_TRICKLE[Math.min(wave, 6)]
      : undefined;

  return { patientCount, ...config, trickleWeights, totalWaves: patientsPerWave.length };
}

export function generateWaveTags(wave: number, patientCount: number): TriageTag[] {
  const cfg = WAVE_CONFIG[Math.min(wave, 6)];
  const immediateCount = Math.round(patientCount * cfg.immediatePercent);
  const expectantCount = Math.round(patientCount * cfg.expectantPercent);
  // At least 1 dead (black) for waves 2+, included within expectant count
  const deadCount = wave >= 2 ? Math.max(1, expectantCount) : expectantCount;
  const remaining = Math.max(0, patientCount - immediateCount - deadCount);
  const delayedCount = Math.round(remaining * (2 / 3));
  const minorCount = remaining - delayedCount;

  const tags: TriageTag[] = [
    ...Array(immediateCount).fill('red' as TriageTag),
    ...Array(deadCount).fill('black' as TriageTag),
    ...Array(delayedCount).fill('yellow' as TriageTag),
    ...Array(minorCount).fill('green' as TriageTag),
  ];

  // Shuffle
  for (let i = tags.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [tags[i], tags[j]] = [tags[j], tags[i]];
  }

  return tags;
}

export function getPerformanceTitle(accuracy: number, saved: number, total: number, rankingsConfig?: { tiers: { title: string; minAccuracy: number; minSaveRate: number }[]; fallback: string }): string {
  const saveRate = total > 0 ? (saved / total) * 100 : 0;
  if (rankingsConfig) {
    for (const tier of rankingsConfig.tiers) {
      if (accuracy >= tier.minAccuracy && saveRate >= tier.minSaveRate) {
        return tier.title;
      }
    }
    return rankingsConfig.fallback;
  }
  // Hardcoded fallback when no config provided
  if (accuracy >= 90 && saveRate >= 80) return '🏆 LEGENDARY TRIAGE COMMANDER';
  if (accuracy >= 80 && saveRate >= 70) return '⭐ ELITE FIELD MEDIC';
  if (accuracy >= 70 && saveRate >= 60) return '🎖️ VETERAN FIRST RESPONDER';
  if (accuracy >= 60 && saveRate >= 50) return '💪 FIELD MEDIC — UNDER PRESSURE';
  if (accuracy >= 50) return '🩹 ROOKIE RESPONDER — ROOM TO GROW';
  return '💀 OVERWHELMED — THE KAIJU WON THIS ROUND';
}
